<script setup lang="ts">
  import VisitorForm from '@/shared/components/rebranding/VisitorForm/VisitorForm.vue'
  import { getAbTest } from '@/shared/composables/useGetAbTest'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
  import { usePrepareInboundAddon } from '@/shared/composables/usePrepareInboundAddon'
  import {
    GENDERS,
    INBOUND_ADDON_CODE,
    INBOUND_ADDON_KEYS,
    OWNER_RELATION,
  } from '@/shared/constants/other.constants'
  import {
    emailResponse,
    inboundCheckResponse,
  } from '@/shared/constants/validationMessages.constants'
  import Visitor from '@/shared/models/Visitor'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'
  import { VisitorType } from '@/shared/types/store.types'
  import { convertStartDateToTimestamp } from '@qicgroupdoha/travel-devkit/helpers/dates'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  interface Emits {
    (e: 'proceed', value: string): void
    (e: 'load', value: () => Promise<void>, requestId?: string, withLoader?: boolean): void
    (e: 'error', value: string): void
  }

  const emit = defineEmits<Emits>()

  const homeUrl = useHomeUrl()
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const router = useRouter()
  const route = useRoute()
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const { checkPrepareInboundAddon } = usePrepareInboundAddon()
  const isLoading = ref(false)
  const emailIsValid = ref(true)

  const holder = computed(() => visitorsStore.holder)
  const getQuote = computed(() => visitorsStore.getQuoteData)
  const countries = computed(() => coreStore.countries)
  const visitDates = computed(() => visitorsStore.getVisitDates)

  const entryType = visitorsStore.getAnalyticsEntryType

  const getPreapprovalVisitor = (visitor: VisitorType): Promise<VisitorType> =>
    visitorsStore
      .getPreapprovalVisa(visitor)
      .then((data) => ({
        ...visitor,
        ...data,
      }))
      .catch(() => ({
        ...visitor,
        visaValidityDate: undefined,
        policyDuration: visitorsStore.entryValue,
      }))

  const getVisitorNextPage = (visitor: VisitorType): string =>
    visitor.visaValidityDate ? PurchaseFlowRoutes.TRAVEL_DURATION : PurchaseFlowRoutes.QUOTE

  const proceed = async (holderInfo: VisitorType) => {
    emit(
      'load',
      async () => {
        let holder = {
          ...holderInfo,
          relationship: OWNER_RELATION.CODE,
          relationshipName: OWNER_RELATION.NAME,
          srNo: holderInfo.srNo === 0 ? 1 : holderInfo.srNo,
        }
        isLoading.value = true

        holder = await getPreapprovalVisitor(holder)

        const countryName = countries.value.find((el) => el.code === holder.country)
        const gender = holder.gender === GENDERS.MALE ? 'male' : 'female'

        await visitorsStore
          .updateVisitor(new Visitor(holder, holder.id))
          .then(() => {
            gaService.sendClickProceedByHolderInfo(
              entryType,
              gender,
              countryName?.desc || '',
              coreStore.getInitialReferrer,
              holder.passport.substring(holder.passport.length - 4)
            )
          })
          .catch((error) => {
            const message = error?.response.data.errorMessage
            emailIsValid.value = message !== emailResponse
            visitorsStore.getTravellers()
          })

        if (!holder.visaValidityDate) {
          if (checkPrepareInboundAddon(holder, visitorsStore.entryValue)) {
            await getAbTest(INBOUND_ADDON_CODE)
          }
        }

        if (emailIsValid.value) {
          const isInboundAddonAbTest = INBOUND_ADDON_KEYS.includes(
            coreStore.getAbValue(INBOUND_ADDON_CODE)
          )
          if (
            isInboundAddonAbTest &&
            !holder.visaValidityDate &&
            checkPrepareInboundAddon(holder, visitorsStore.entryValue)
          ) {
            const res = await visitorsStore
              .inboundAddonCheck(
                holder.passport,
                visitDates.value.startDate,
                visitDates.value.tillDate
              )
              .catch((error) => {
                const errorCode = error?.response?.data?.errorCode
                if (errorCode !== inboundCheckResponse) {
                  emit('error', error)
                }
              })
            visitorsStore.setInboundAddon(res?.success || false)
          } else {
            visitorsStore.setInboundAddon(false)
          }
          emit('proceed', getVisitorNextPage(holder))
        }

        isLoading.value = false
      },
      'holderInfo',
      false
    )
  }

  const sendFormData = async (startDate: Date, entryId: string, numadults = 1) => {
    await visitorsStore.sendFormDataStepped({
      policyDate: convertStartDateToTimestamp(startDate),
      travelType: entryId,
      numadults,
    })
  }

  const checksInfo = async () => {
    if (Object.prototype.hasOwnProperty.call(getQuote.value, 'schemes') === false) {
      window.location.href = homeUrl
      return
    }

    if (!visitorsStore.visitDate || !visitorsStore.entryValue) {
      return router.push({
        name: PurchaseFlowRoutes.TRAVEL_DATE,
        query: route.query,
      })
    }

    await Promise.all([
      sendFormData(
        visitorsStore.visitDate,
        visitorsStore.entryValue,
        visitorsStore.numberOfVisitors
      ),
      coreStore.loadStoreData(),
      visitorsStore.getTravellers(),
    ])
  }

  onMounted(() => {
    emit(
      'load',
      async () => {
        await checksInfo()
        onPerformanceCheckEnd()
      },
      'holder-info-load'
    )
  })
</script>

<template>
  <VisitorForm
    :visitor="holder"
    :email-is-valid="emailIsValid"
    :is-loading="isLoading"
    is-holder
    @save="proceed"
    @set-email-is-valid="emailIsValid = true"
  />
</template>
